import * as i0 from '@angular/core';
import { Injectable, Component, Input, NgModule } from '@angular/core';
import tinycolor from 'tinycolor2';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/flex-layout/flex';
import * as i3 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { FlexLayoutModule } from '@angular/flex-layout';

class CidaasHostedPageColorService {
    /**
     * A function to generate the color palette for material design.
     *
     * The type parameter should be the type of Color you give.
     * At the moment only 'primary' and 'secondary' are supported
     *
     * The hex parameter should be the hex code of the color, for which
     * you want to generate the color palette
     *
     * The algorithm parameter is setting the algorithm, which will
     * be taken to generate the color palette.
     * Currently there are 3 algorithms supported:
     * 'default', 'constantin' and 'buckner'
     *
     * @param hex main color code
     * @param type color type (primary, accent)
     * @param [algorithm] algorithm to calculate colors
     * @return color pallete of main color code in angular material specification
     */
    computeColors(hex, type = '', algorithm) {
        // Return array of color objects.
        const baseLight = tinycolor('#ffffff');
        const baseDark = this.multiply(tinycolor(hex).toRgb(), tinycolor(hex).toRgb());
        const baseTriad = tinycolor(hex).tetrad();
        let palette = [];
        switch (algorithm) {
            case 'constantin':
                palette = [
                    this.getColorObject(tinycolor.mix(baseLight, hex, 12), '50'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 30), '100'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 50), '200'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 70), '300'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 85), '400'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 100), '500'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 87), '600'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 70), '700'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 54), '800'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 25), '900'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(80).lighten(65), 'A100'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(80).lighten(55), 'A200'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(100).lighten(45), 'A400'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[4], 15).saturate(100).lighten(40), 'A700')
                ];
                break;
            case 'buckner':
                palette = [
                    this.getColorObject(tinycolor.mix(baseLight, hex, 12), '50'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 30), '100'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 50), '200'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 70), '300'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 85), '400'),
                    this.getColorObject(tinycolor.mix(baseLight, hex, 100), '500'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 87), '600'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 70), '700'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 54), '800'),
                    this.getColorObject(tinycolor.mix(baseDark, hex, 25), '900'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(48), 'A100'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(80).lighten(36), 'A200'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(31), 'A400'),
                    this.getColorObject(tinycolor.mix(baseDark, baseTriad[3], 15).saturate(100).lighten(28), 'A700')
                ];
                break;
            default:
                palette = [
                    this.getColorObject(tinycolor(hex).lighten(52), '50'),
                    this.getColorObject(tinycolor(hex).lighten(37), '100'),
                    this.getColorObject(tinycolor(hex).lighten(26), '200'),
                    this.getColorObject(tinycolor(hex).lighten(12), '300'),
                    this.getColorObject(tinycolor(hex).lighten(6), '400'),
                    this.getColorObject(tinycolor(hex), '500'),
                    this.getColorObject(tinycolor(hex).darken(6), '600'),
                    this.getColorObject(tinycolor(hex).darken(12), '700'),
                    this.getColorObject(tinycolor(hex).darken(18), '800'),
                    this.getColorObject(tinycolor(hex).darken(24), '900'),
                    this.getColorObject(tinycolor(hex).lighten(50).saturate(30), 'A100'),
                    this.getColorObject(tinycolor(hex).lighten(30).saturate(30), 'A200'),
                    this.getColorObject(tinycolor(hex).lighten(10).saturate(15), 'A400'),
                    this.getColorObject(tinycolor(hex).lighten(5).saturate(5), 'A700')
                ];
                break;
        }
        const fontLight = getComputedStyle(document.documentElement).getPropertyValue('--font-color-light');
        const fontDark = getComputedStyle(document.documentElement).getPropertyValue('--font-color');
        const length = palette.length;
        for (let i = 0; i < length; i++) {
            const key = '--theme-' + type + (type ? '-' : '') + 'contrast-' + palette[i].name;
            palette[i].name = '--theme-' + type + (type ? '-' : '') + palette[i].name;
            const contrastColor = (palette[i].darkContrast) ? fontDark : fontLight;
            palette.push(this.getColorObject(tinycolor(contrastColor), key));
        }
        return palette;
    }
    // Utility function to combine two rgb objects via Multiply
    multiply(rgb1, rgb2) {
        rgb1.r = Math.floor(rgb1.r * rgb2.r / 255);
        rgb1.g = Math.floor(rgb1.g * rgb2.g / 255);
        rgb1.b = Math.floor(rgb1.b * rgb2.b / 255);
        return tinycolor('rgb ' + rgb1.r + ' ' + rgb1.g + ' ' + rgb1.b);
    }
    getColorObject(value, name) {
        const c = tinycolor(value);
        return {
            name,
            hex: c.toHexString(),
            // darkContrast: c.isLight()
            // This will consider wide range of color codes in the darker side.
            darkContrast: c.getBrightness() < 142 ? false : true
        };
    }
    static { this.ɵfac = function CidaasHostedPageColorService_Factory(t) { return new (t || CidaasHostedPageColorService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CidaasHostedPageColorService, factory: CidaasHostedPageColorService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CidaasHostedPageColorService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], null, null); })();

var ComponentLayout;
(function (ComponentLayout) {
    ComponentLayout["HORIZONTAL"] = "row";
    ComponentLayout["VERTICAL"] = "column";
})(ComponentLayout || (ComponentLayout = {}));

var ComponentLocation;
(function (ComponentLocation) {
    ComponentLocation["START"] = "start";
    ComponentLocation["CENTER"] = "center";
    ComponentLocation["END"] = "end";
    ComponentLocation["SPACEBETWEEN"] = "space-between";
})(ComponentLocation || (ComponentLocation = {}));

var ComponentTarget;
(function (ComponentTarget) {
    ComponentTarget["BLANK"] = "_blank";
    ComponentTarget["PARENT"] = "_parent";
    ComponentTarget["SELF"] = "_self";
    ComponentTarget["TOP"] = "_top";
})(ComponentTarget || (ComponentTarget = {}));

function CidaasHostedPageFooterComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "p");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵstyleProp("color", ctx_r0.footerStyle.fontColor)("font-family", ctx_r0.footerStyle.fontFamily)("font-size", ctx_r0.footerStyle.fontSize);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(ctx_r0.companyName);
} }
function CidaasHostedPageFooterComponent_a_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "a", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("color", ctx_r1.footerStyle.fontColor)("font-family", ctx_r1.footerStyle.fontFamily)("font-size", ctx_r1.footerStyle.fontSize);
    i0.ɵɵpropertyInterpolate("href", ctx_r1.imprintUri, i0.ɵɵsanitizeUrl);
    i0.ɵɵattribute("target", ctx_r1.footerStyle.target);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(2, 9, "FOOTER.IMPRINT_LINK_LABEL"));
} }
function CidaasHostedPageFooterComponent_a_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "a", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("color", ctx_r2.footerStyle.fontColor)("font-family", ctx_r2.footerStyle.fontFamily)("font-size", ctx_r2.footerStyle.fontSize);
    i0.ɵɵpropertyInterpolate("href", ctx_r2.tosUri, i0.ɵɵsanitizeUrl);
    i0.ɵɵattribute("target", ctx_r2.footerStyle.target);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(2, 9, "FOOTER.TOS_LINK_LABEL"));
} }
function CidaasHostedPageFooterComponent_a_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "a", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("color", ctx_r3.footerStyle.fontColor)("font-family", ctx_r3.footerStyle.fontFamily)("font-size", ctx_r3.footerStyle.fontSize);
    i0.ɵɵpropertyInterpolate("href", ctx_r3.policyUri, i0.ɵɵsanitizeUrl);
    i0.ɵɵattribute("target", ctx_r3.footerStyle.target);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(2, 9, "FOOTER.POLICY_LINK_LABEL"));
} }
function CidaasHostedPageFooterComponent_div_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7)(1, "p");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("background-color", ctx_r4.footerStyle.backgroundColor);
    i0.ɵɵpropertyInterpolate("fxLayoutAlign", ctx_r4.componentLocation.CENTER);
    i0.ɵɵadvance(1);
    i0.ɵɵstyleProp("color", ctx_r4.footerStyle.fontColor)("font-family", ctx_r4.footerStyle.fontFamily)("font-size", ctx_r4.footerStyle.fontSize);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1("Version ", ctx_r4.appVersion, "");
} }
class CidaasHostedPageFooterComponent {
    constructor() {
        this.componentLocation = ComponentLocation;
        this.componentLayout = ComponentLayout;
        this.componentTarget = ComponentTarget;
        this.defaultFooterStyle = {
            layout: ComponentLayout.HORIZONTAL,
            location: ComponentLocation.CENTER,
            target: ComponentTarget.BLANK,
            height: '100%'
        };
    }
    ngOnInit() {
        this.footerStyle = this.generateComponentStyle();
        this.footerImprintAllignment = this.footerStyle.layout === ComponentLayout.VERTICAL ?
            this.footerStyle.location : ComponentLocation.START;
        this.footerTosAllignment = this.footerStyle.layout === ComponentLayout.VERTICAL ?
            this.footerStyle.location : ComponentLocation.CENTER;
        this.footerPolicyAllignment = this.footerStyle.layout === ComponentLayout.VERTICAL ?
            this.footerStyle.location : ComponentLocation.END;
    }
    generateComponentStyle() {
        if (!this.customStyle) {
            return this.defaultFooterStyle;
        }
        const styles = Object.assign(this.defaultFooterStyle, this.customStyle);
        if (this.companyName && styles.layout === ComponentLayout.HORIZONTAL) {
            styles.location = ComponentLocation.SPACEBETWEEN;
        }
        return styles;
    }
    static { this.ɵfac = function CidaasHostedPageFooterComponent_Factory(t) { return new (t || CidaasHostedPageFooterComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: CidaasHostedPageFooterComponent, selectors: [["lib-cidaas-hosted-page-footer"]], inputs: { tosUri: "tosUri", imprintUri: "imprintUri", policyUri: "policyUri", companyName: "companyName", appVersion: "appVersion", customStyle: "customStyle" }, decls: 10, vars: 16, consts: [[1, "footer", 3, "fxLayout", "fxLayout.lt-sm", "fxLayoutAlign"], [4, "ngIf"], [3, "fxLayout", "fxLayout.lt-sm"], [1, "txt-center"], [3, "color", "font-family", "font-size", "href", 4, "ngIf"], [3, "fxLayoutAlign", "background-color", 4, "ngIf"], [3, "href"], [3, "fxLayoutAlign"]], template: function CidaasHostedPageFooterComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵtemplate(1, CidaasHostedPageFooterComponent_div_1_Template, 3, 7, "div", 1);
            i0.ɵɵelementStart(2, "div", 2)(3, "div", 3);
            i0.ɵɵtemplate(4, CidaasHostedPageFooterComponent_a_4_Template, 3, 11, "a", 4);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(5, "div", 3);
            i0.ɵɵtemplate(6, CidaasHostedPageFooterComponent_a_6_Template, 3, 11, "a", 4);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(7, "div", 3);
            i0.ɵɵtemplate(8, CidaasHostedPageFooterComponent_a_8_Template, 3, 11, "a", 4);
            i0.ɵɵelementEnd()()();
            i0.ɵɵtemplate(9, CidaasHostedPageFooterComponent_div_9_Template, 3, 10, "div", 5);
        } if (rf & 2) {
            i0.ɵɵstyleProp("height", ctx.footerStyle.height)("background-color", ctx.footerStyle.backgroundColor);
            i0.ɵɵpropertyInterpolate("fxLayout", ctx.footerStyle.layout);
            i0.ɵɵpropertyInterpolate("fxLayout.lt-sm", ctx.componentLayout.VERTICAL);
            i0.ɵɵpropertyInterpolate2("fxLayoutAlign", "", ctx.footerStyle.location, " ", ctx.componentLocation.CENTER, "");
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.companyName);
            i0.ɵɵadvance(1);
            i0.ɵɵpropertyInterpolate2("fxLayout", "", ctx.footerStyle.layout, " ", ctx.componentLocation.CENTER, "");
            i0.ɵɵpropertyInterpolate("fxLayout.lt-sm", ctx.componentLayout.VERTICAL);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.imprintUri);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.tosUri);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.policyUri);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("ngIf", ctx.appVersion);
        } }, dependencies: [i1.NgIf, i2.DefaultLayoutDirective, i2.DefaultLayoutAlignDirective, i3.TranslatePipe], styles: [".footer[_ngcontent-%COMP%]{padding:.8em}.footer[_ngcontent-%COMP%]   .txt-center[_ngcontent-%COMP%]{text-align:center!important}.footer[_ngcontent-%COMP%]   .nav-tem[_ngcontent-%COMP%]{text-align:center!important;padding:0 10px!important;word-break:break-all!important}p[_ngcontent-%COMP%]{margin:0;padding:1rem 0}"] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CidaasHostedPageFooterComponent, [{
        type: Component,
        args: [{ selector: 'lib-cidaas-hosted-page-footer', template: "<div class=\"footer\" [style.height]=\"footerStyle.height\" [style.background-color]=\"footerStyle.backgroundColor\"\n  fxLayout=\"{{footerStyle.layout}}\" fxLayout.lt-sm=\"{{componentLayout.VERTICAL}}\"\n  fxLayoutAlign=\"{{footerStyle.location}} {{componentLocation.CENTER}}\">\n  <div *ngIf=\"companyName\">\n    <p [style.color]=\"footerStyle.fontColor\" [style.font-family]=\"footerStyle.fontFamily\"\n      [style.font-size]=\"footerStyle.fontSize\">{{companyName}}</p>\n  </div>\n  <div fxLayout=\"{{footerStyle.layout}} {{componentLocation.CENTER}}\" fxLayout.lt-sm=\"{{componentLayout.VERTICAL}}\">\n    <div class=\"txt-center\">\n      <a *ngIf=\"imprintUri\" [attr.target]=\"footerStyle.target\" [style.color]=\"footerStyle.fontColor\" [style.font-family]=\"footerStyle.fontFamily\"\n        [style.font-size]=\"footerStyle.fontSize\" href=\"{{imprintUri}}\">{{'FOOTER.IMPRINT_LINK_LABEL' | translate}}</a>\n    </div>\n    <div class=\"txt-center\">\n      <a *ngIf=\"tosUri\" [attr.target]=\"footerStyle.target\" [style.color]=\"footerStyle.fontColor\" [style.font-family]=\"footerStyle.fontFamily\"\n        [style.font-size]=\"footerStyle.fontSize\" href=\"{{tosUri}}\">{{'FOOTER.TOS_LINK_LABEL' | translate}}</a>\n    </div>\n    <div class=\"txt-center\">\n      <a *ngIf=\"policyUri\" [attr.target]=\"footerStyle.target\" [style.color]=\"footerStyle.fontColor\" [style.font-family]=\"footerStyle.fontFamily\"\n        [style.font-size]=\"footerStyle.fontSize\" href=\"{{policyUri}}\">{{'FOOTER.POLICY_LINK_LABEL' | translate}}</a>\n    </div>\n  </div>\n</div>\n<div *ngIf=\"appVersion\" fxLayoutAlign=\"{{componentLocation.CENTER}}\"\n  [style.background-color]=\"footerStyle.backgroundColor\">\n  <p [style.color]=\"footerStyle.fontColor\" [style.font-family]=\"footerStyle.fontFamily\"\n    [style.font-size]=\"footerStyle.fontSize\">Version {{appVersion}}</p>\n</div>\n", styles: [".footer{padding:.8em}.footer .txt-center{text-align:center!important}.footer .nav-tem{text-align:center!important;padding:0 10px!important;word-break:break-all!important}p{margin:0;padding:1rem 0}\n"] }]
    }], function () { return []; }, { tosUri: [{
            type: Input
        }], imprintUri: [{
            type: Input
        }], policyUri: [{
            type: Input
        }], companyName: [{
            type: Input
        }], appVersion: [{
            type: Input
        }], customStyle: [{
            type: Input
        }] }); })();

class CidaasHostedPageFooterModule {
    static { this.ɵfac = function CidaasHostedPageFooterModule_Factory(t) { return new (t || CidaasHostedPageFooterModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: CidaasHostedPageFooterModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule,
            FlexLayoutModule,
            TranslateModule] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CidaasHostedPageFooterModule, [{
        type: NgModule,
        args: [{
                declarations: [CidaasHostedPageFooterComponent],
                imports: [
                    CommonModule,
                    FlexLayoutModule,
                    TranslateModule
                ],
                exports: [CidaasHostedPageFooterComponent]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CidaasHostedPageFooterModule, { declarations: [CidaasHostedPageFooterComponent], imports: [CommonModule,
        FlexLayoutModule,
        TranslateModule], exports: [CidaasHostedPageFooterComponent] }); })();

/*
 * Public API Surface of cidaas-hosted-page-toolkit
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CidaasHostedPageColorService, CidaasHostedPageFooterComponent, CidaasHostedPageFooterModule };
